import MainContextProvider from "context/MainContextProvider";
import React, { lazy, Suspense, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";

import PublicRoute from "../../../app/auth/PublicRoute";
import RoleBasedRoute from "../../../app/auth/RoleBasedRoute";
import {
  AsyncAddNewVideoEditForm,
  AsyncAddNewVideoUploadingForm,
  AsyncArchiveClipsComponent,
  AsyncArchiveSettingsComponent,
  AsyncArchiveStreamComponent,
  AsyncBulkEditorComponent,
  AsyncClientProfile,
  AsyncClipRatingRule,
  AsyncConnectedApps,
  AsyncCopyStreamContainer,
  AsyncDashboard,
  AsyncDataConsumed,
  AsyncEditVideoFormContainer,
  AsyncGroupMappingDetailComponent,
  AsyncInternalStatus,
  AsyncMyHighlightsComponent,
  AsyncMyStoriesComponent,
  AsyncOrganizationManagement,
  AsyncOverview,
  AsyncPermissionManagement,
  AsyncProcessTrackingComponent,
  AsyncPublishClipsComponent,
  AsyncPublishHistory,
  AsyncQueueSettingComponent,
  AsyncReportsComponent,
  AsyncResetPassword,
  AsyncScoreBoardContainer,
  AsyncSingleWebstoryEditorComponent,
  AsyncStreamDetailsContainer,
  AsyncThirdPartyContainer,
  AsyncUpdateStorageComponent,
  AsyncUserManagement,
  AsyncUserProfileBasicInfoEditComponent,
  AsyncUserProfileComponent,
  AsyncUserProfileUpdatePasswordEditComponent,
  AsyncVideoPlayerContainer,
  AsyncVideosInfoNComponent,
  AsyncWorkspaceManagement,
} from "../../../app/LazyRoutes";
import QRHandler from "../../../app/QRHandler";
import TitleUpdater from "../../../components/common/TitleUpdater/TitleUpdater";
import CookieContainer from "../../../components/v2/Cookies";
import ErrorFallbackComponent from "../../../components/v2/ErrorFallbackComponent";
import FullScreenLoader from "../../../components/v2/Loaders/FullScreenLoader";
import { allUserRoles, cdRoles, clientRoles } from "../../../constants/phase";
import { APP_PAGE } from "../../../constants/v2/appConstants";
import { SocketContext, webhookSocket } from "../../../context/socket";
import { addRenderingErrorToDataDogRum } from "../../../externals/datadog";
import { addStylesheet } from "../../../utils";
import retryComponent from "../../../utils/retryComponent";
import CookiePolicyComponent from "../../../views/CookiePolicy/container";
import CustomerAgreementComponent from "../../../views/CustomerAgreement/container";
import FTPcomponent from "../../../views/FTPpublish/container";
import PrivacyPoliciesComponent from "../../../views/PrivacyPolicies/container";
import OldPrivateRoute from "../../../views/PrivateRoute/container";
import PrivateRoleBasedRoute from "./PrivateRoleBasedRoute";
import PrivateRoute from "./PrivateRoute";
import RestrictedPrivateRoute from "./RestrictedPrivateRoute";

const LoginV2Component = lazy(() => retryComponent(() => import(/* webpackChunkName: "login" */ "../../login")));
const LoginV2 = () => <LoginV2Component />;
const SignupV2Component = lazy(() => retryComponent(() => import(/* webpackChunkName: "signup" */ "../../signup")));
const SignupV2 = () => <SignupV2Component />;
const SSOLoginV2Component = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "sso-login" */ "../../sso-login")),
);
const SSOLoginV2 = () => <SSOLoginV2Component />;
const ForgotPasswordV2Component = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "forgot-password" */ "../../forgot-password")),
);
const ForgotPasswordV2 = () => <ForgotPasswordV2Component />;

const PreviewVideo = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "preview-video" */ "../../preview-video")),
);
const Dashboard = lazy(() => retryComponent(() => import(/* webpackChunkName: "dashboard" */ "../../dashboard")));
const Videos = lazy(() => retryComponent(() => import(/* webpackChunkName: "videos" */ "../../videos")));
const Clips = lazy(() => retryComponent(() => import(/* webpackChunkName: "clips" */ "../../clips")));
const LiveVideoEditor = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "live-video" */ "../../live-video")),
);
const ClipInfo = lazy(() => retryComponent(() => import(/* webpackChunkName: "clip-info" */ "../../clip-info")));
const QuickHighlight = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "quick-highlights" */ "../../quick-highlight")),
);
const MyHighlights = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "my-highlights" */ "../../my-highlights")),
);
const AddVideo = lazy(() => retryComponent(() => import(/* webpackChunkName: "add-video" */ "../../add-video")));
const OrganizationProfile = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "organization-profile" */ "../../organization-profile")),
);
const RuleListing = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "rule-listing" */ "../../rule-listing")),
);
const RuleOutputs = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "rule-outputs" */ "../../rule-outputs")),
);
const CombineRuleInfo = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "combine-rule-info" */ "../../combine-rule-info")),
);
const Studio = lazy(() => retryComponent(() => import(/* webpackChunkName: "studio" */ "../../studio")));
const StudioClips = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "studio-clips" */ "../../studio-clips")),
);
const StudioHighlights = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "studio-highlights" */ "../../studio-highlights")),
);
const StudioPublish = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "studio-publish" */ "../../studio-publish")),
);
const StudioTemplates = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "studio-templates" */ "../../studio-templates")),
);
const StudioThumbnailTemplate = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "studio-thumbnail-template" */ "../../studio-thumbnail-template")),
);
const StudioSubtitlesTemplate = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "studio-subtitles-template" */ "../../studio-subtitles-template")),
);
const RuleConfiguration = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "rule-details" */ "../../rule-configuration")),
);
const Archive = lazy(() => retryComponent(() => import(/* webpackChunkName: "archive" */ "../../archive")));
const Configuration = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "configuration" */ "../../configuration")),
);
const StudioProjects = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "studio-projects" */ "../../studio-projects")),
);
const ChannelSummary = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "channel-summary" */ "../../channel-summary")),
);
const SingleWebStoryEditor = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "single-webstory-editor" */ "../../single-webstory-editor")),
);

const MatchSchedule = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "match-schedule" */ "../../match-schedule")),
);

const MatchScheduleUploadCSV = lazy(() =>
  retryComponent(
    () => import(/* webpackChunkName: "match-schedule-upload-csv" */ "../../match-schedule/MatchScheduleUploadCsv"),
  ),
);

const ThirdPartyIntegrationCallback = lazy(() =>
  retryComponent(
    () => import(/* webpackChunkName: "third-party-integration-callback" */ "../../third-party-integration-callback"),
  ),
);

const PublishHistory = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "publish-history" */ "../../publish-history")),
);

const VideoPlayer = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "video-player" */ "../../video-player")),
);

const PlatformCallback = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "platform-callback" */ "../../platform-callback")),
);

const Graphics = lazy(() => retryComponent(() => import(/* webpackChunkName: "graphics" */ "../../graphics")));

const TournamentConfig = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "tournament-config" */ "../../tournament-config")),
);

const VideoPlayerVJS = lazy(() =>
  retryComponent(() => import(/* webpackChunkName: "video-player-vjs" */ "../../video-player-vjs")),
);

const StreamPage = lazy(() => retryComponent(() => import(/* webpackChunkName: "stream" */ "../../stream")));

const Home = () => {
  //get url params
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const isQRCode = urlParams.get("source");

  //if req comes from qr code
  return isQRCode === "qrcode" ? <QRHandler /> : <Navigate to="/login" />;
};

const AppV2 = () => {
  useEffect(() => {
    //TODO: Remove it from here and move it to index.html once this version is stable
    addStylesheet(
      "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&display=swap",
    );
  }, []);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallbackComponent} onError={addRenderingErrorToDataDogRum}>
      <SocketContext.Provider value={{ webhookSocket }}>
        <Suspense fallback={<FullScreenLoader />}>
          <Router>
            <Routes>
              <Route
                element={
                  <>
                    <CookieContainer />
                    <TitleUpdater />
                  </>
                }
              >
                <Route exact path="/" element={<Home />} />
                <Route exact path="/preview-video" element={<PreviewVideo />} />
                <Route exact path="/sso-login" element={<PublicRoute element={SSOLoginV2} />} />
                <Route exact path="/login" element={<PublicRoute element={LoginV2} />} />
                <Route exact path="/forgot-password" element={<PublicRoute element={ForgotPasswordV2} />} />
                <Route exact path="/signup" element={<PublicRoute element={SignupV2} />} />
                <Route exact path="/reset/:token" element={<PublicRoute element={AsyncResetPassword} />} />
                <Route exact path="/customer-agreement" element={<CustomerAgreementComponent />} />
                <Route exact path="/privacy-policies" element={<PrivacyPoliciesComponent />} />
                <Route exact path="/cookie-policy" element={<CookiePolicyComponent />} />
                <Route
                  exact
                  path="/update-password-edit/:id"
                  element={<AsyncUserProfileUpdatePasswordEditComponent />}
                />
                <Route exact path="/:platform/callback" element={<PlatformCallback />} />
                <Route
                  element={
                    <MainContextProvider>
                      <PrivateRoute />
                    </MainContextProvider>
                  }
                >
                  <Route element={<RestrictedPrivateRoute passThrough={true} />}>
                    <Route exact path="/live-edit/:streamId" element={<LiveVideoEditor page={APP_PAGE.LIVE_EDIT} />} />
                    <Route exact path="/edit-clips/:streamId" element={<Clips page={APP_PAGE.EDIT_CLIPS} />} />
                    <Route
                      exact
                      path="/internal-schedule"
                      element={<MatchSchedule page={APP_PAGE.INTERNAL_SCHEDULE} />}
                    />
                    <Route
                      exact
                      path="/internal-schedule/upload-csv"
                      element={<MatchScheduleUploadCSV page={APP_PAGE.INTERNAL_SCHEDULE_UPLOAD_CSV} />}
                    />
                  </Route>

                  <Route
                    exact
                    path="/third-party-integration-callback/:integration"
                    element={<ThirdPartyIntegrationCallback page={APP_PAGE.THIRD_PARTY_INTEGRATION_CALLBACK} />}
                  />
                  <Route exact path="/add-video" element={<AddVideo page={APP_PAGE.ADD_VIDEO} />} />
                  <Route exact path="/edit-video/:streamId" element={<AddVideo page={APP_PAGE.EDIT_VIDEO} />} />
                  <Route
                    exact
                    path="/single-webstory-editor/:clipListId"
                    element={<SingleWebStoryEditor page={APP_PAGE.WEB_STORY} />}
                  />
                  <Route exact path="/video-player" element={<VideoPlayer page={APP_PAGE.VIDEO_PLAYER} />} />
                  <Route exact path="/video-player-vjs" element={<VideoPlayerVJS page={APP_PAGE.VIDEO_PLAYER} />} />
                  <Route exact path="/publish-history" element={<PublishHistory page={APP_PAGE.PUBLISH_HISTORY} />} />
                  <Route
                    exact
                    path="/match-schedule/upload-csv"
                    element={<MatchScheduleUploadCSV page={APP_PAGE.MATCH_SCHEDULE_UPLOAD_CSV} />}
                  />
                  <Route exact path="/match-schedule" element={<MatchSchedule page={APP_PAGE.MATCH_SCHEDULE} />} />

                  <Route
                    exact
                    path="/organizations-profile/:id/:active_tab"
                    element={<OrganizationProfile page={APP_PAGE.ORGANIZATION_PROFILE} />}
                  />
                  <Route
                    exact
                    path="/organizations-profile/:id/:active_tab/:active_sub_tab"
                    element={<OrganizationProfile page={APP_PAGE.ORGANIZATION_PROFILE} />}
                  />
                  <Route
                    exact
                    path="/workspaces-profile/:id/:active_tab"
                    element={<OrganizationProfile page={APP_PAGE.ORGANIZATION_PROFILE} />}
                  />
                  <Route
                    exact
                    path="/workspaces-profile/:id/:active_tab/:active_sub_tab"
                    element={<OrganizationProfile page={APP_PAGE.ORGANIZATION_PROFILE} />}
                  />
                  <Route
                    exact
                    path="/configuration/:active_tab"
                    element={<Configuration page={APP_PAGE.CONFIGURATION} />}
                  />
                  <Route exact path="/channel-summary" element={<ChannelSummary />} />
                  <Route
                    element={
                      <PrivateRoleBasedRoute
                        allowedRoles={[...Object.values(clientRoles), ...Object.values(cdRoles)]}
                      />
                    }
                  >
                    <Route exact path="/dashboard" element={<Dashboard page={APP_PAGE.DASHBOARD} />} />
                  </Route>
                  <Route exact path="/videos" element={<Videos page={APP_PAGE.VIDEOS} />} />
                  <Route exact path="/clips/:streamId" element={<Clips page={APP_PAGE.CLIPS} />} />
                  <Route exact path="/stream/:streamId" element={<StreamPage page={APP_PAGE.STREAM} />} />
                  <Route exact path="/live-video/:streamId" element={<LiveVideoEditor page={APP_PAGE.LIVE_VIDEO} />} />
                  <Route exact path="/clip-info/:clipId" element={<ClipInfo page={APP_PAGE.CLIP_EDITOR} />} />
                  <Route
                    exact
                    path="/editor-page/:clipListId"
                    element={<QuickHighlight page={APP_PAGE.HIGHLIGHT_EDITOR} />}
                  />
                  <Route exact path="/quick-highlight" element={<QuickHighlight page={APP_PAGE.HIGHLIGHT_EDITOR} />} />
                  <Route
                    exact
                    path="/quick-highlight/:clipListId"
                    element={<QuickHighlight page={APP_PAGE.HIGHLIGHT_EDITOR} />}
                  />
                  <Route exact path="/my-highlights" element={<MyHighlights page={APP_PAGE.HIGHLIGHTS} />} />
                  <Route
                    element={
                      <PrivateRoleBasedRoute
                        allowedRoles={[...Object.values(allUserRoles).filter((role) => role !== allUserRoles.reviewer)]}
                      />
                    }
                  >
                    <Route exact path="/rule-listing" element={<RuleListing page={APP_PAGE.RULE_LISTING} />} />
                    <Route exact path="/rule-outputs" element={<RuleOutputs page={APP_PAGE.RULE_OUTPUTS} />} />
                  </Route>
                  <Route
                    element={
                      <PrivateRoleBasedRoute
                        allowedRoles={[...Object.values(allUserRoles).filter((role) => role !== allUserRoles.reviewer)]}
                      />
                    }
                  >
                    <Route
                      exact
                      path="/rule-configuration/:ruleId"
                      page={APP_PAGE.RULE_EDITOR}
                      element={<RuleConfiguration isNewRule={false} />}
                    />
                  </Route>

                  <Route
                    element={
                      <PrivateRoleBasedRoute
                        allowedRoles={[...Object.values(allUserRoles).filter((role) => role !== allUserRoles.reviewer)]}
                      />
                    }
                  >
                    <Route
                      exact
                      path="/rule-configuration-v2/:ruleId"
                      page={APP_PAGE.RULE_EDITOR}
                      element={<RuleConfiguration isNewRule={false} v2={true} />}
                    />
                  </Route>

                  <Route
                    element={
                      <PrivateRoleBasedRoute
                        allowedRoles={[...Object.values(allUserRoles).filter((role) => role !== allUserRoles.reviewer)]}
                      />
                    }
                  >
                    <Route
                      exact
                      path="/rule-configuration/new"
                      page={APP_PAGE.RULE_EDITOR}
                      element={<RuleConfiguration isNewRule={true} />}
                    />
                  </Route>

                  <Route
                    element={
                      <PrivateRoleBasedRoute
                        allowedRoles={[...Object.values(allUserRoles).filter((role) => role !== allUserRoles.reviewer)]}
                      />
                    }
                  >
                    <Route
                      exact
                      path="/rule-configuration-v2/new"
                      page={APP_PAGE.RULE_EDITOR}
                      element={<RuleConfiguration isNewRule={true} v2={true} />}
                    />
                  </Route>

                  <Route exact path="/studio" element={<Studio page={APP_PAGE.STUDIO} />} />
                  <Route exact path="/studio/clips" element={<StudioClips page={APP_PAGE.STUDIO} />} />
                  <Route exact path="/studio/projects" element={<StudioProjects page={APP_PAGE.STUDIO} />} />
                  <Route exact path="/studio/highlights" element={<StudioHighlights page={APP_PAGE.STUDIO} />} />
                  <Route exact path="/studio/templates" element={<StudioTemplates page={APP_PAGE.STUDIO} />} />
                  <Route
                    exact
                    path="/studio/thumbnail-template/:templateId"
                    element={<StudioThumbnailTemplate page={APP_PAGE.THUMBNAIL_TEMPLATE} />}
                  />
                  <Route
                    exact
                    path="/studio/graphics-template/:templateId"
                    element={<StudioThumbnailTemplate page={APP_PAGE.GRAPHICS_TEMPLATE} />}
                  />
                  <Route
                    exact
                    path="/studio/subtitles-template/:templateId"
                    element={<StudioSubtitlesTemplate page={APP_PAGE.STUDIO} />}
                  />
                  <Route
                    exact
                    path="/studio/publish/:projectId/:streamId"
                    element={<StudioPublish page={APP_PAGE.STUDIO} />}
                  />
                  <Route
                    exact
                    path="/combinee-rule-info/:ruleId"
                    element={<CombineRuleInfo page={APP_PAGE.RULE_BASED_STREAMS} />}
                  />
                  <Route exact path="/archive" element={<Archive page={APP_PAGE.ARCHIVAL} />} />
                  <Route exact path="/graphics" element={<Graphics page={APP_PAGE.GRAPHICS} />} />
                  <Route
                    exact
                    path="/tournament-config/:tournamentId"
                    element={<TournamentConfig page={APP_PAGE.TOURNAMENT_CONFIG} />}
                  />
                </Route>
                <Route element={<OldPrivateRoute />}>
                  <Route exact path="/add-video" element={<AsyncAddNewVideoUploadingForm />} />
                  <Route exact path="/edit-video/:id" element={<AsyncEditVideoFormContainer />} />
                  <Route
                    exact
                    path="/dashboard"
                    element={
                      <RoleBasedRoute
                        element={AsyncDashboard}
                        allowedRoles={[...Object.values(clientRoles), ...Object.values(cdRoles)]}
                      />
                    }
                  />
                  <Route
                    exact
                    path="/overview"
                    element={
                      <RoleBasedRoute
                        element={AsyncOverview}
                        allowedRoles={[allUserRoles.superAdmin, allUserRoles.admin, ...Object.values(cdRoles)]}
                      />
                    }
                  />
                  <Route
                    exact
                    path="/user-management"
                    element={
                      <RoleBasedRoute
                        element={<AsyncUserManagement opsView={false} />}
                        allowedRoles={[allUserRoles.superAdmin]}
                      />
                    }
                  />
                  <Route
                    exact
                    path="/ops-management"
                    element={
                      <RoleBasedRoute
                        element={<AsyncUserManagement opsView={true} />}
                        allowedRoles={[allUserRoles.superAdmin]}
                      />
                    }
                  />
                  <Route
                    exact
                    path="/organization-management"
                    element={
                      <RoleBasedRoute element={AsyncOrganizationManagement} allowedRoles={[allUserRoles.superAdmin]} />
                    }
                  />
                  <Route
                    exact
                    path="/workspace-management"
                    element={
                      <RoleBasedRoute element={AsyncWorkspaceManagement} allowedRoles={[allUserRoles.superAdmin]} />
                    }
                  />
                  <Route exact path="/organization-profile/:id" element={AsyncClientProfile} />
                  <Route exact path="/permission-management" element={<AsyncPermissionManagement />} />
                  <Route exact path="/user-profile/:id" element={<AsyncUserProfileComponent />} />
                  <Route exact path="/basic-info-edit/:id" element={<AsyncUserProfileBasicInfoEditComponent />} />
                  <Route exact path="/dashboard-consumption-calculator-list" element={<AsyncDataConsumed />} />
                  <Route exact path="/archive-streams" element={<AsyncArchiveStreamComponent />} />
                  <Route exact path="/archive-entities/:id" element={<AsyncArchiveClipsComponent />} />
                  <Route exact path="/copy-stream" element={<AsyncCopyStreamContainer />} />
                  <Route exact path="/video-player" element={<AsyncVideoPlayerContainer />} />
                  <Route exact path="/social-media/share" element={<AsyncThirdPartyContainer />} />
                  <Route exact path="/connected-apps" element={<AsyncConnectedApps />} />
                  <Route exact path="/reports" element={<AsyncReportsComponent />} />
                  <Route exact path="/update-storage/" element={<AsyncUpdateStorageComponent />} />
                  <Route exact path="/highlights/:id" element={<AsyncMyHighlightsComponent />} />
                  <Route exact path="/stories/:id" element={<AsyncMyStoriesComponent />} />
                  <Route exact path="/clip-publish/:id" element={<AsyncPublishClipsComponent />} />
                  <Route exact path="/group-mapping-key/:mappingKey" element={<AsyncGroupMappingDetailComponent />} />
                  <Route exact path="/clip-info/:clipID" element={<AsyncVideosInfoNComponent />} />
                  <Route exact path="/bulk-edits" element={<AsyncBulkEditorComponent />} />
                  <Route exact path="/client-archive" element={<AsyncArchiveSettingsComponent />} />
                  <Route exact path="/clip-info/scoreboard/:clipID" element={<AsyncScoreBoardContainer />} />
                  <Route exact path="/single-webstory-editor/:id" element={<AsyncSingleWebstoryEditorComponent />} />
                  <Route exact path="/stream-details/:id" element={<AsyncStreamDetailsContainer />} />
                  <Route exact path="/ftp-publish/:id" element={<FTPcomponent />} />
                  <Route exact path="/queue-setting" element={<AsyncQueueSettingComponent />} />
                  <Route exact path="/vod-queue-setting" element={<AsyncQueueSettingComponent />} />
                  <Route exact path="/process-tracking" element={<AsyncProcessTrackingComponent />} />
                  <Route exact path="/add-video/:id" element={<AsyncAddNewVideoEditForm />} />
                  <Route exact path="/publish-history" element={<AsyncPublishHistory />} />
                  <Route exact path="/internal-status" element={<AsyncInternalStatus />} />
                  <Route exact path="/clip-rating-rule" element={<AsyncClipRatingRule />} />
                  <Route exact path="/permission-management" element={<AsyncPermissionManagement />} />
                </Route>
                {/* Catch-all route to redirect to the dashboard */}
                <Route path="*" element={<Navigate to="/dashboard" />} />
              </Route>
            </Routes>
          </Router>
        </Suspense>
      </SocketContext.Provider>
    </ErrorBoundary>
  );
};

export default AppV2;
